import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Palette, Image, Globe, Mail, Save, RefreshCw } from 'lucide-react';
import axios from '../../lib/axios';
import { toast } from 'sonner';
import { useBranding } from '../../lib/BrandingContext';
import { getErrorMessage } from '../../utils/errorHelpers';

const BrandingManagement = () => {
  const navigate = useNavigate();
  const { branding: currentBranding, refreshBranding } = useBranding();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    logo_url: '',
    primary_color: '#1e40af',
    secondary_color: '#3b82f6',
    accent_color: '#60a5fa',
    favicon_url: '',
    login_background: '',
    footer_text: '',
    email_sender_name: 'TechOrange-Legal Opinion System',
    company_name: '',
    company_address: '',
    support_email: '',
    support_phone: ''
  });

  useEffect(() => {
    fetchBranding();
  }, []);

  const fetchBranding = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/admin/branding');
      setFormData(response.data);
    } catch (error) {
      console.error('Error fetching branding:', error);
      toast.error(getErrorMessage(error, 'Failed to load branding configuration'));
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await axios.put('/api/admin/branding', formData);
      
      // Refresh branding context to apply changes immediately
      await refreshBranding();
      
      toast.success('Branding updated successfully! Changes will be visible on next page load.');
    } catch (error) {
      console.error('Error saving branding:', error);
      const errorMsg = getErrorMessage(error, 'Failed to save branding');
      toast.error(errorMsg);
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    if (window.confirm('Are you sure you want to reset to default branding?')) {
      setFormData({
        logo_url: '',
        primary_color: '#1e40af',
        secondary_color: '#3b82f6',
        accent_color: '#60a5fa',
        favicon_url: '',
        login_background: '',
        footer_text: '© 2025 TechOrange-Legal. All rights reserved.',
        email_sender_name: 'TechOrange-Legal Opinion System',
        company_name: 'Legal Opinion AI',
        company_address: '',
        support_email: 'support@legalopinion.ai',
        support_phone: ''
      });
      toast.info('Branding reset to defaults. Click Save to apply.');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading branding settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/admin')}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-5 h-5 text-gray-600" />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Branding Management</h1>
                <p className="text-sm text-gray-600">Customize your white-label branding</p>
              </div>
            </div>
            <div className="flex space-x-3">
              <button
                onClick={handleReset}
                className="flex items-center px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Reset to Default
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex items-center px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
              >
                <Save className="w-4 h-4 mr-2" />
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Settings Form */}
          <div className="lg:col-span-2 space-y-6">
            {/* Visual Branding */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-center mb-4">
                <Palette className="w-5 h-5 text-blue-600 mr-2" />
                <h2 className="text-lg font-semibold text-gray-900">Visual Branding</h2>
              </div>

              <div className="space-y-4">
                {/* Company Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Company Name
                  </label>
                  <input
                    type="text"
                    value={formData.company_name}
                    onChange={(e) => handleChange('company_name', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="e.g., ICICI Bank Legal Services"
                  />
                </div>

                {/* Colors */}
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Primary Color
                    </label>
                    <div className="flex items-center space-x-2">
                      <input
                        type="color"
                        value={formData.primary_color}
                        onChange={(e) => handleChange('primary_color', e.target.value)}
                        className="h-10 w-16 border border-gray-300 rounded cursor-pointer"
                      />
                      <input
                        type="text"
                        value={formData.primary_color}
                        onChange={(e) => handleChange('primary_color', e.target.value)}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                        placeholder="#1e40af"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Secondary Color
                    </label>
                    <div className="flex items-center space-x-2">
                      <input
                        type="color"
                        value={formData.secondary_color}
                        onChange={(e) => handleChange('secondary_color', e.target.value)}
                        className="h-10 w-16 border border-gray-300 rounded cursor-pointer"
                      />
                      <input
                        type="text"
                        value={formData.secondary_color}
                        onChange={(e) => handleChange('secondary_color', e.target.value)}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                        placeholder="#3b82f6"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Accent Color
                    </label>
                    <div className="flex items-center space-x-2">
                      <input
                        type="color"
                        value={formData.accent_color}
                        onChange={(e) => handleChange('accent_color', e.target.value)}
                        className="h-10 w-16 border border-gray-300 rounded cursor-pointer"
                      />
                      <input
                        type="text"
                        value={formData.accent_color}
                        onChange={(e) => handleChange('accent_color', e.target.value)}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                        placeholder="#60a5fa"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Images & Assets */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-center mb-4">
                <Image className="w-5 h-5 text-blue-600 mr-2" />
                <h2 className="text-lg font-semibold text-gray-900">Images & Assets</h2>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Logo URL
                  </label>
                  <input
                    type="url"
                    value={formData.logo_url}
                    onChange={(e) => handleChange('logo_url', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="https://example.com/logo.png"
                  />
                  <p className="mt-1 text-xs text-gray-500">Recommended: 200x80px, PNG with transparent background</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Favicon URL
                  </label>
                  <input
                    type="url"
                    value={formData.favicon_url}
                    onChange={(e) => handleChange('favicon_url', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="https://example.com/favicon.ico"
                  />
                  <p className="mt-1 text-xs text-gray-500">Recommended: 32x32px or 64x64px ICO/PNG</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Login Background Image URL
                  </label>
                  <input
                    type="url"
                    value={formData.login_background}
                    onChange={(e) => handleChange('login_background', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="https://example.com/background.jpg"
                  />
                  <p className="mt-1 text-xs text-gray-500">Optional: High-resolution image for login page background</p>
                </div>
              </div>
            </div>

            {/* Content & Communication */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-center mb-4">
                <Mail className="w-5 h-5 text-blue-600 mr-2" />
                <h2 className="text-lg font-semibold text-gray-900">Content & Communication</h2>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Footer Text
                  </label>
                  <input
                    type="text"
                    value={formData.footer_text}
                    onChange={(e) => handleChange('footer_text', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="© 2025 Your Company. All rights reserved."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email Sender Name
                  </label>
                  <input
                    type="text"
                    value={formData.email_sender_name}
                    onChange={(e) => handleChange('email_sender_name', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Legal Opinion System"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Company Address
                  </label>
                  <textarea
                    value={formData.company_address}
                    onChange={(e) => handleChange('company_address', e.target.value)}
                    rows={3}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter your company address"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Support Email
                    </label>
                    <input
                      type="email"
                      value={formData.support_email}
                      onChange={(e) => handleChange('support_email', e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="support@example.com"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Support Phone
                    </label>
                    <input
                      type="tel"
                      value={formData.support_phone}
                      onChange={(e) => handleChange('support_phone', e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="+1 (555) 123-4567"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Preview Panel */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-sm p-6 sticky top-8">
              <div className="flex items-center mb-4">
                <Globe className="w-5 h-5 text-blue-600 mr-2" />
                <h2 className="text-lg font-semibold text-gray-900">Preview</h2>
              </div>

              <div className="space-y-4">
                {/* Color Preview */}
                <div>
                  <p className="text-sm font-medium text-gray-700 mb-2">Colors</p>
                  <div className="flex space-x-2">
                    <div className="flex-1 text-center">
                      <div
                        className="h-16 rounded-lg border border-gray-200"
                        style={{ backgroundColor: formData.primary_color }}
                      />
                      <p className="text-xs text-gray-500 mt-1">Primary</p>
                    </div>
                    <div className="flex-1 text-center">
                      <div
                        className="h-16 rounded-lg border border-gray-200"
                        style={{ backgroundColor: formData.secondary_color }}
                      />
                      <p className="text-xs text-gray-500 mt-1">Secondary</p>
                    </div>
                    <div className="flex-1 text-center">
                      <div
                        className="h-16 rounded-lg border border-gray-200"
                        style={{ backgroundColor: formData.accent_color }}
                      />
                      <p className="text-xs text-gray-500 mt-1">Accent</p>
                    </div>
                  </div>
                </div>

                {/* Logo Preview */}
                {formData.logo_url && (
                  <div>
                    <p className="text-sm font-medium text-gray-700 mb-2">Logo</p>
                    <div className="border border-gray-200 rounded-lg p-4 bg-gray-50 flex items-center justify-center">
                      <img
                        src={formData.logo_url}
                        alt="Logo Preview"
                        className="max-h-20 max-w-full"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'block';
                        }}
                      />
                      <p className="text-sm text-gray-500" style={{ display: 'none' }}>
                        Failed to load logo
                      </p>
                    </div>
                  </div>
                )}

                {/* Company Info */}
                <div>
                  <p className="text-sm font-medium text-gray-700 mb-2">Company Info</p>
                  <div className="space-y-2 text-sm">
                    <p className="font-semibold">{formData.company_name || 'Legal Opinion AI'}</p>
                    {formData.support_email && (
                      <p className="text-gray-600">{formData.support_email}</p>
                    )}
                    {formData.support_phone && (
                      <p className="text-gray-600">{formData.support_phone}</p>
                    )}
                  </div>
                </div>

                {/* Sample Button */}
                <div>
                  <p className="text-sm font-medium text-gray-700 mb-2">Button Style</p>
                  <button
                    className="w-full py-2 rounded-lg text-white font-semibold"
                    style={{ backgroundColor: formData.primary_color }}
                  >
                    Sample Button
                  </button>
                </div>

                {/* Footer Preview */}
                <div>
                  <p className="text-sm font-medium text-gray-700 mb-2">Footer</p>
                  <p className="text-xs text-gray-600 text-center p-3 bg-gray-50 rounded-lg">
                    {formData.footer_text || 'Powered by Legal Opinion AI'}
                  </p>
                </div>
              </div>

              {/* Test Login Page Button */}
              <div className="mt-6 pt-6 border-t border-gray-200">
                <button
                  onClick={() => window.open('/login', '_blank')}
                  className="w-full py-2 border border-blue-600 text-blue-600 rounded-lg hover:bg-blue-50 transition-colors font-semibold"
                >
                  Preview Login Page
                </button>
                <p className="text-xs text-gray-500 text-center mt-2">
                  Opens in new tab after saving
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BrandingManagement;
